import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminHeader } from '@/features/admin/ui';
import { adminQueries } from '@/lib/queries/admin';

export const Route = createFileRoute('/admin/audit')({
  component: AuditPage,
});

function AuditPage() {
  const [page, setPage] = useState(0);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const list = useQuery(adminQueries.audit(page));
  const rows = list.data ?? [];

  const filtered = rows.filter((r) => {
    const actorOk = actor.trim()
      ? (r.actor?.display_name ?? '').toLowerCase().includes(actor.toLowerCase()) ||
        (r.actor?.username ?? '').toLowerCase().includes(actor.toLowerCase())
      : true;
    const actionOk = action.trim() ? r.action.toLowerCase().includes(action.toLowerCase()) : true;
    return actorOk && actionOk;
  });

  const exportCsv = () => {
    // Proper CSV quoting: wrap every field, escape inner quotes by doubling —
    // so a comma or quote in the value (incl. valid-JSON metadata) survives.
    const field = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = 'timestamp,actor,action,target_table,target_id,metadata\n';
    const body = filtered
      .map((r) =>
        [
          new Date(r.created_at).toISOString(),
          r.actor?.username ?? r.actor_id ?? '',
          r.action,
          r.target_table ?? '',
          r.target_id ?? '',
          JSON.stringify(r.metadata ?? {}),
        ]
          .map(field)
          .join(','),
      )
      .join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-page-${page + 1}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex items-center justify-between gap-4">
        <AdminHeader kicker="Admin · Accountability" title="Audit Log" />
        <button
          type="button"
          onClick={exportCsv}
          className="shrink-0 rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood"
        >
          Export CSV
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Filter this page by actor"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          className="rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
        <input
          type="search"
          placeholder="…by action"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : (
        <table className="mt-6 w-full border-collapse font-sans text-sm">
          <thead>
            <tr className="border-b border-ink/15 text-left text-xs uppercase tracking-[0.12em] text-ink/50">
              <th className="py-2 pr-3">When</th>
              <th className="py-2 pr-3">Actor</th>
              <th className="py-2 pr-3">Action</th>
              <th className="py-2 pr-3">Target</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-ink/5 align-top">
                <td className="py-2 pr-3 text-ink/60">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-2 pr-3 text-ink/80">
                  {r.actor?.display_name ?? r.actor?.username ?? '—'}
                </td>
                <td className="py-2 pr-3 text-ink">{r.action}</td>
                <td className="py-2 pr-3 text-ink/60">
                  {r.target_table ? `${r.target_table}` : '—'}
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-oxblood">metadata</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-[0.7rem] text-ink/50">
                      {JSON.stringify(r.metadata, null, 2)}
                    </pre>
                  </details>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="py-8 text-center text-ink/50">
                  No entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs text-ink disabled:opacity-40"
        >
          Previous
        </button>
        <span className="font-sans text-xs text-ink/50">Page {page + 1}</span>
        <button
          type="button"
          disabled={rows.length < 50}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs text-ink disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
